import React, { useState } from "react"
import { Box, Tab, Tabs } from "@mui/material"
import NotificationSnackbar from "@/components/ui/NotificationSnackbar"
import { PANEL_SCROLLBAR_SX } from "@/theme/panelStyles"
import VesselSpecTab from "@/features/voyagePlanner/wizard/tabs/VesselSpecTab"
import VoyageSpecTab from "@/features/voyagePlanner/wizard/tabs/VoyageSpecTab"
import SpeedProfileTab from "@/features/voyagePlanner/wizard/tabs/SpeedProfileTab"
import ConsumptionTab from "@/features/voyagePlanner/wizard/tabs/ConsumptionTab"

const TAB_SX = {
  textTransform: "none",
  fontSize: "11.5px",
  fontWeight: 600,
  color: "#5b7f9c",
  minHeight: 36,
  minWidth: 0,
  px: 1,
  "&.Mui-selected": { color: "#ed6c02" },
}

// The specification tabs (formerly the Specifications modal) — the voyage
// planner's Options step, also embedded in the RNW analyser's specifications
// view. Vessel data is edited inline on the Vessel tab; voyage settings edit
// the draft copy. showProfileTabs hides the read-only Speed Profile /
// Consumption tabs (the RNW analyser doesn't use them).
function OptionsStep({
  specTab,
  onSpecTabChange,
  draftPlannerVesselSettings,
  setDraftPlannerVesselSettings,
  optionsErrors,
  setOptionsError,
  vesselLabel,
  vesselMeta,
  vesselId,
  speedProfileData,
  consumptionProfileData,
  calculatorBasespeed,
  defaultPlannerVesselSettings,
  showProfileTabs = true,
  bunkerPrices,
  setBunkerPrices,
  currentDraft,
  setCurrentDraft,
  routingInactive = false,
}) {
  const [snackOpen, setSnackOpen] = useState(false)

  return (
    <Box
      data-tour="planner-options"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
      }}
    >
      {/* Fixed header: the tab bar stays put while the tab body scrolls. */}
      <Tabs
        value={specTab}
        onChange={(_e, value) => onSpecTabChange(value)}
        variant="fullWidth"
        sx={{
          flexShrink: 0,
          minHeight: 36,
          borderBottom: "1px solid #e2e8ee",
          "& .MuiTabs-indicator": { backgroundColor: "#ed6c02", height: 2 },
        }}
      >
        <Tab label="Vessel" sx={TAB_SX} />
        <Tab label="Voyage" sx={TAB_SX} />
        {showProfileTabs && <Tab label="Speed Profile" sx={TAB_SX} />}
        {showProfileTabs && <Tab label="Consumption" sx={TAB_SX} />}
      </Tabs>

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          ...PANEL_SCROLLBAR_SX,
        }}
      >
        {specTab === 0 && (
          <VesselSpecTab
            vesselMeta={vesselMeta}
            defaultPlannerVesselSettings={defaultPlannerVesselSettings}
            vesselLabel={vesselLabel}
            vesselId={vesselId}
            setDraftPlannerVesselSettings={setDraftPlannerVesselSettings}
            onVesselSaved={() => setSnackOpen(true)}
          />
        )}
        {specTab === 1 && (
          <VoyageSpecTab
            draftPlannerVesselSettings={draftPlannerVesselSettings}
            setDraftPlannerVesselSettings={setDraftPlannerVesselSettings}
            optionsErrors={optionsErrors}
            setOptionsError={setOptionsError}
            bunkerPrices={bunkerPrices}
            setBunkerPrices={setBunkerPrices}
            routingInactive={routingInactive}
            vesselMeta={vesselMeta}
          />
        )}
        {specTab === 2 && <SpeedProfileTab speedProfileData={speedProfileData} />}
        {specTab === 3 && (
          <ConsumptionTab
            consumptionProfileData={consumptionProfileData}
            calculatorBasespeed={calculatorBasespeed}
            currentDraft={currentDraft}
            setCurrentDraft={setCurrentDraft}
          />
        )}
      </Box>

      <NotificationSnackbar
        open={snackOpen}
        severity="success"
        message="Vessel data saved successfully"
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        showCloseButton={false}
      />
    </Box>
  )
}

export default React.memo(OptionsStep)
